import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { defineSection } from "@/ssr/define-section";
import { blogPostsQueryOptions } from "@/api-queries/blog";
import { Img } from "@/components/ui/img";
import { GeneratedCover } from "@/components/blog/GeneratedCover";
import { AuthorAvatar } from "@/components/blog/AuthorAvatar";
import { readingMinutes } from "@/lib/reading-time";
import { isProblemTag, PROBLEM_TAG_LABELS } from "@/lib/matching/taxonomy";
import { blogCardExcerpt } from "@/lib/blog-excerpt";

const homeBlogQuery = blogPostsQueryOptions({ status: "published", limit: 3, page: 1 });

interface HomeBlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content?: string;
  readingMinutes?: number;
  category?: string | null;
  coverImageUrl?: string | null;
  authorName?: string | null;
  authorImage?: string | null;
  publishedAt?: string | null;
  createdAt: string;
}

const ACCENT = "oklch(37.12% 0.059 186.62)";
const DOT = "oklch(74.72% 0.089 82.39)";

function PostCover({ post, className, sizes }: { post: HomeBlogPost; className: string; sizes: string }) {
  return post.coverImageUrl ? (
    <Img src={post.coverImageUrl} alt={post.title} sizes={sizes} className={className} loading="lazy" />
  ) : (
    <GeneratedCover slug={post.slug} category={post.category} className={className} />
  );
}

function MetaRow({ post, size = "sm" }: { post: HomeBlogPost; size?: "sm" | "lg" }) {
  const label = isProblemTag(post.category) ? PROBLEM_TAG_LABELS[post.category] : null;
  const minutes = post.readingMinutes ?? (post.content ? readingMinutes(post.content) : null);
  return (
    <div
      className={`flex flex-wrap items-center gap-2 font-bold uppercase tracking-[0.12em] ${size === "lg" ? "text-[11px]" : "text-[10px]"}`}
      style={{ color: "oklch(45% 0.02 84)" }}
    >
      {label && <span style={{ color: ACCENT }}>{label}</span>}
      {label && minutes && <span className="size-1 rounded-full" style={{ background: DOT }} />}
      {minutes && <span>{minutes} min</span>}
    </div>
  );
}

function BlogPreviewView() {
  const ref = useScrollReveal<HTMLElement>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [queryInput, setQueryInput] = useState("");

  const { data } = useQuery(homeBlogQuery);
  const posts: HomeBlogPost[] = data?.data ?? (Array.isArray(data) ? data : []);

  // Hide the whole section until there are at least 3 published posts.
  if (posts.length < 3) return null;

  const featured = posts[0]!;
  const sidePosts = posts.slice(1, 3);
  const featuredExcerpt = blogCardExcerpt(featured);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = queryInput.trim();
    navigate({ to: "/blog", search: q ? { q } : {} });
  };

  return (
    <section
      ref={ref}
      className="relative overflow-hidden py-24 sm:py-32"
      style={{ background: "oklch(95.43% 0.0136 78.26)" }}
    >
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center">
          <h2
            className="font-black leading-[0.95] tracking-[-0.035em] text-foreground"
            style={{ fontFamily: "var(--font-heading)", fontSize: "clamp(2.25rem, 4.5vw, 3.5rem)" }}
          >
            {t("home.blog.title")}
          </h2>

          {/* Search */}
          <form
            onSubmit={submitSearch}
            className="mx-auto mt-8 flex max-w-md items-center gap-2 rounded-full border bg-white px-4 py-2.5 shadow-sm"
            style={{ borderColor: "oklch(82% 0.03 84)" }}
          >
            <MagnifyingGlass className="size-4 shrink-0 text-muted-foreground" />
            <input
              type="search"
              value={queryInput}
              onChange={(e) => setQueryInput(e.target.value)}
              placeholder={t("home.blog.searchPlaceholder")}
              className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </form>
        </div>

        {/* Magazine grid */}
        <div className="mt-14 grid gap-8 lg:grid-cols-[1.4fr_1fr]">
          {/* Featured */}
          <Link
            to="/blog/$slug"
            params={{ slug: featured.slug }}
            className="group flex flex-col overflow-hidden rounded-3xl border bg-white transition-shadow hover:shadow-lg"
            style={{ borderColor: "oklch(82% 0.03 84)" }}
          >
            <div className="aspect-[16/9] overflow-hidden">
              <PostCover
                post={featured}
                sizes="(min-width: 1280px) 710px, (min-width: 1024px) 58vw, 100vw"
                className="size-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              />
            </div>
            <div className="flex flex-1 flex-col p-8">
              <MetaRow post={featured} size="lg" />
              <h3
                className="mt-4 italic"
                style={{
                  fontFamily: "var(--font-heading)",
                  fontWeight: 400,
                  fontSize: "clamp(1.5rem, 2.2vw, 2.25rem)",
                  lineHeight: 1.05,
                  letterSpacing: "-0.015em",
                }}
              >
                {featured.title}
              </h3>
              {featuredExcerpt && (
                <p className="mt-4 text-[15px] leading-relaxed text-muted-foreground line-clamp-3">
                  {featuredExcerpt}
                </p>
              )}
              {featured.authorName && (
                <div className="mt-auto flex items-center gap-3 pt-6">
                  <AuthorAvatar name={featured.authorName} image={featured.authorImage} size={36} />
                  <div className="text-[13px] font-extrabold tracking-[-0.005em]">{featured.authorName}</div>
                </div>
              )}
            </div>
          </Link>

          {/* Side stack — two vertically stacked cards, 16:9 cover on top */}
          <div className="flex flex-col gap-6">
            {sidePosts.map((post) => (
              <SidePostCard key={post.id} post={post} />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 flex justify-end">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2.5 rounded-full px-6 py-3.5 text-[13px] font-bold transition-transform hover:-translate-y-0.5"
            style={{ background: ACCENT, color: "oklch(98% 0 0)" }}
          >
            {t("home.blog.viewAll")}
          </Link>
        </div>
      </div>
    </section>
  );
}

function SidePostCard({ post }: { post: HomeBlogPost }) {
  const excerpt = blogCardExcerpt(post);

  return (
    <Link
      to="/blog/$slug"
      params={{ slug: post.slug }}
      className="group flex flex-1 flex-col overflow-hidden rounded-3xl border bg-white transition-shadow hover:shadow-lg"
      style={{ borderColor: "oklch(82% 0.03 84)" }}
    >
      <div className="aspect-[16/9] overflow-hidden">
        <PostCover
          post={post}
          sizes="(min-width: 1280px) 500px, (min-width: 1024px) 40vw, 100vw"
          className="size-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
        />
      </div>
      <div className="flex flex-1 flex-col p-6">
        <MetaRow post={post} />
        <h4
          className="mt-2 italic"
          style={{
            fontFamily: "var(--font-heading)",
            fontWeight: 400,
            fontSize: "clamp(1.1rem, 1.5vw, 1.4rem)",
            lineHeight: 1.15,
            letterSpacing: "-0.01em",
          }}
        >
          {post.title}
        </h4>
        {excerpt && (
          <p className="mt-2 line-clamp-2 text-[13px] leading-relaxed text-muted-foreground">
            {excerpt}
          </p>
        )}
      </div>
    </Link>
  );
}

export const BlogPreview = defineSection({
  Component: BlogPreviewView,
  queries: [homeBlogQuery],
});
